// frontend/src/admin/blockforms/ContainerForm.js — FULL REPLACEMENT (Delivery 2.5)
// Adds:
//   - 3 key-point inputs per language (manual fill)
//   - "Generate with AI" per language: calls /card-ai/generate, saves to DB,
//     and fills summary + key points into the form for review/editing
//     (only available when editing an existing container that has blocks)
//   - When editing, loads ALL fields (both languages) from the new
//     /content-page/container/{type}/{id}/full endpoint — fixes the old bug
//     where saving an edit wiped short_summary_hi.

import React, { useState, useEffect } from 'react';
import { buildBlockSave } from './blockSaveTarget';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

const PARENT_ID_FIELD = { topic: 'topic_id', subtopic: 'subtopic_id', chunk: 'chunk_id' };

const emptyPoints = ['', '', ''];
const toThree = (arr) => {
    const a = Array.isArray(arr) ? arr.map((p) => String(p)) : [];
    return [a[0] || '', a[1] || '', a[2] || ''];
};
const cleanPoints = (arr) => arr.map((p) => p.trim()).filter(Boolean);

const ContainerForm = ({ level, childLevel, entityId, nextOrderIndex, editingContainer, onSaved, onCancel, onDrillInto }) => {
    const [name, setName] = useState(editingContainer?.heading || '');
    const [featureImage, setFeatureImage] = useState(editingContainer?.feature_image_url || '');
    const [summaryEn, setSummaryEn] = useState('');
    const [summaryHi, setSummaryHi] = useState('');
    const [pointsEn, setPointsEn] = useState(emptyPoints);
    const [pointsHi, setPointsHi] = useState(emptyPoints);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(null); // 'en' | 'hi' | null
    const [error, setError] = useState(null);
    const [loadingFull, setLoadingFull] = useState(!!editingContainer);

    // When editing: load full bilingual data so nothing gets wiped on save
    useEffect(() => {
        if (!editingContainer) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/content-page/container/${editingContainer.card_type}/${editingContainer.card_id}/full`);
                if (!res.ok) throw new Error('Failed to load current values');
                const d = await res.json();
                if (cancelled) return;
                setName(d.name || '');
                setFeatureImage(d.feature_image_url || '');
                setSummaryEn(d.short_summary_en || '');
                setSummaryHi(d.short_summary_hi || '');
                setPointsEn(toThree(d.key_points_en));
                setPointsHi(toThree(d.key_points_hi));
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoadingFull(false);
            }
        })();
        return () => { cancelled = true; };
    }, [editingContainer]);

    useEffect(() => {
        const hasUnsavedChanges = name || featureImage || summaryEn || summaryHi ||
            cleanPoints(pointsEn).length > 0 || cleanPoints(pointsHi).length > 0;
        if (!hasUnsavedChanges) return;
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [name, featureImage, summaryEn, summaryHi, pointsEn, pointsHi]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch(`${API_BASE}/rich-content/upload-image`, {
                method: 'POST',
                body: formData
            });
            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            setFeatureImage(data.file_path);
        } catch (err) {
            setError('Image upload failed');
        } finally {
            setUploading(false);
        }
    };

    // ── AI generation (edit mode only: needs an existing card with blocks) ──
    const handleGenerate = async (lang) => {
        if (!editingContainer) return;
        setError(null);
        setGenerating(lang);
        try {
            const res = await fetch(`${API_BASE}/card-ai/generate/${editingContainer.card_type}/${editingContainer.card_id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ language: lang, overwrite: true })
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'AI generation failed');
            }
            const data = await res.json();
            if (data.skipped) {
                setError('Not enough content in this card yet — add content blocks first, then generate.');
                return;
            }
            if (lang === 'en') {
                setSummaryEn(data.short_summary || '');
                setPointsEn(toThree(data.key_points));
            } else {
                setSummaryHi(data.short_summary || '');
                setPointsHi(toThree(data.key_points));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setGenerating(null);
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        setError(null);
        setSaving(true);

        const kpEn = cleanPoints(pointsEn);
        const kpHi = cleanPoints(pointsHi);

        try {
            if (editingContainer) {
                const res = await fetch(`${API_BASE}/content-page/container/${editingContainer.card_type}/${editingContainer.card_id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name,
                        feature_image_url: featureImage || null,
                        short_summary_en: summaryEn || null,
                        short_summary_hi: summaryHi || null,
                        key_points_en: kpEn.length ? kpEn : null,
                        key_points_hi: kpHi.length ? kpHi : null
                    })
                });
                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.detail || 'Save failed');
                }
                onSaved();
            } else {
                const payload = {
                    level: childLevel,
                    parent_ids: { [PARENT_ID_FIELD[level]]: entityId },
                    name,
                    order_index: nextOrderIndex,
                    feature_image_url: featureImage || null,
                    short_summary_en: summaryEn || null,
                    short_summary_hi: summaryHi || null
                };
                const res = await fetch(`${API_BASE}/content-page/container`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
                if (!res.ok) {
                    const err = await res.json();
                    throw new Error(err.detail || 'Save failed');
                }
                const data = await res.json();
                onSaved();
                if (onDrillInto) {
                    onDrillInto(data.card_type, data.card_id, name);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const renderKeyPoints = (points, setPoints, label) => (
        <>
            <label>{label}</label>
            {points.map((p, i) => (
                <input
                    key={i}
                    type="text"
                    value={p}
                    placeholder={`Key point ${i + 1}`}
                    style={{ marginBottom: 6 }}
                    onChange={(e) => {
                        const next = [...points];
                        next[i] = e.target.value;
                        setPoints(next);
                    }}
                />
            ))}
        </>
    );

    return (
        <div className="ace-form">
            <h4>{editingContainer ? `Edit ${editingContainer.card_type.replace('_', ' ')}` : `Insert ${childLevel.replace('_', ' ')}`}</h4>
            {error && <div className="ace-error">{error}</div>}
            {loadingFull && <p style={{ color: '#6b7280' }}>Loading current values...</p>}

            <label>Name / Heading *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} />

            <label>Feature Image (optional)</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
            {featureImage && (
                <div className="ace-image-preview">
                    <img src={featureImage.startsWith('http') ? featureImage : `${API_BASE}/${featureImage}`} alt="" />
                </div>
            )}

            {editingContainer ? (
                <div style={{ display: 'flex', gap: 8, margin: '10px 0' }}>
                    <button type="button" onClick={() => handleGenerate('en')} disabled={generating !== null}>
                        {generating === 'en' ? 'Generating...' : '✦ Generate with AI (EN)'}
                    </button>
                    <button type="button" onClick={() => handleGenerate('hi')} disabled={generating !== null}>
                        {generating === 'hi' ? 'Generating...' : '✦ Generate with AI (HI)'}
                    </button>
                </div>
            ) : (
                <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    Tip: after saving and adding content blocks, reopen this form to auto-generate summary &amp; key points with AI.
                </p>
            )}

            <label>Short Summary (English)</label>
            <textarea rows={2} value={summaryEn} onChange={(e) => setSummaryEn(e.target.value)} />
            {renderKeyPoints(pointsEn, setPointsEn, 'Key Points — English (3)')}

            <label>Short Summary (Hindi, optional)</label>
            <textarea rows={2} value={summaryHi} onChange={(e) => setSummaryHi(e.target.value)} />
            {renderKeyPoints(pointsHi, setPointsHi, 'Key Points — Hindi (3, optional)')}

            <div className="ace-form-actions">
                <button onClick={handleSave} disabled={saving || loadingFull}>{saving ? 'Saving...' : (editingContainer ? 'Save' : 'Save & Open')}</button>
                <button onClick={onCancel} className="ace-cancel-btn">Cancel</button>
            </div>
        </div>
    );
};

export default ContainerForm;